import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import Tab from 'react-bootstrap/Tab';

const SUPPLY_TYPES = [
  { value: 'supply-op', label: 'Supply OP' },
  { value: 'supply-7', label: 'Supply 7 days (TTO)' },
  { value: 'supply-14', label: 'Supply 14 days (TTO)' },
  { value: 'supply-28', label: 'Supply 28 days (TTO)' },
  { value: 'blister-pack', label: 'Blister pack' },
  { value: 'medication-at-home', label: 'Medication at home' },
  { value: 'no-supply', label: 'No supply required' },
  { value: 'other', label: 'Other (specify)' },
];

const CHANGE_STATUSES = [
  { value: 'continued', label: 'Continued' },
  { value: 'dose-changed', label: 'Dose changed' },
  { value: 'new', label: 'New (started this admission)' },
  { value: 'stopped', label: 'Stopped on discharge' },
];

const DISCHARGE_DESTINATIONS = [
  'Usual place of residence',
  'Care home',
  'Community hospital',
  'Other',
];

const createTtoId = () => `tto-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const formatIsoDateForInput = (date) => date.toISOString().slice(0, 10);

const EMPTY_NEW_DRUG = { drug: '', dose: '', route: '', frequency: '', changeStatus: 'new', changeReason: '', supplyType: '', supplyDetails: '', gpMonitoring: '' };
const EMPTY_DISCHARGE = { dischargeDate: '', destination: '', consultant: '', wardSpecialty: '', primaryDiagnosis: '', secondaryDiagnoses: [''], clinicalSummary: '', planForGP: '', communityPharmacyOdsCode: '', communityPharmacyName: '' };

function supplyLabel(value) {
  return SUPPLY_TYPES.find((s) => s.value === value)?.label || value || '';
}
function changeLabel(value) {
  return CHANGE_STATUSES.find((s) => s.value === value)?.label || value || '';
}

function generatePrintHTML({ ttoMedications, dischargeDetails, patient, allergies, prescriberName, tenantDisplayName }) {
  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const activeMeds = (ttoMedications || []).filter((m) => m.changeStatus !== 'stopped');
  const stoppedMeds = (ttoMedications || []).filter((m) => m.changeStatus === 'stopped');
  const allergyList = (allergies || []).map((a) => `${a.drug || a.name || ''}${a.reaction ? ` (${a.reaction})` : ''}`).filter(Boolean).join(', ') || 'None documented';

  const row = (label, value) => (!value ? '' : `<tr><td style="font-weight:bold;padding:5px 8px;border:1px solid #ccc;width:220px;vertical-align:top">${label}</td><td style="padding:5px 8px;border:1px solid #ccc">${value}</td></tr>`);

  const medTableRows = activeMeds.map((m) => `
    <tr>
      <td style="padding:5px 8px;border:1px solid #ddd;font-weight:bold">${m.drug || ''}</td>
      <td style="padding:5px 8px;border:1px solid #ddd">${[m.dose, m.route, m.frequency].filter(Boolean).join(' · ')}</td>
      <td style="padding:5px 8px;border:1px solid #ddd">${changeLabel(m.changeStatus)}${m.changeDescription ? `<br><em>${m.changeDescription}</em>` : ''}</td>
      <td style="padding:5px 8px;border:1px solid #ddd">${m.changeReason || ''}</td>
      <td style="padding:5px 8px;border:1px solid #ddd">${supplyLabel(m.supplyType)}${m.supplyDetails ? `<br>${m.supplyDetails}` : ''}</td>
      <td style="padding:5px 8px;border:1px solid #ddd">${m.gpMonitoring || ''}</td>
    </tr>`).join('');

  const medTable = activeMeds.length === 0 ? '<p style="font-size:13px;color:#555">No medications on discharge.</p>' : `
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px">
      <thead><tr style="background:#1a3a5c;color:white">
        <th style="padding:6px 8px;text-align:left">Drug</th>
        <th style="padding:6px 8px;text-align:left">Dose · Route · Frequency</th>
        <th style="padding:6px 8px;text-align:left">Change</th>
        <th style="padding:6px 8px;text-align:left">Reason</th>
        <th style="padding:6px 8px;text-align:left">Supply</th>
        <th style="padding:6px 8px;text-align:left">GP notes</th>
      </tr></thead>
      <tbody>${medTableRows}</tbody>
    </table>`;

  const stoppedSection = stoppedMeds.length === 0 ? '' : `
    <h2 style="font-size:15px;color:#1a3a5c;border-bottom:2px solid #1a3a5c;padding-bottom:4px;margin-top:20px">Discontinued medications</h2>
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px">
      <thead><tr style="background:#1a3a5c;color:white">
        <th style="padding:6px 8px;text-align:left">Medication</th>
        <th style="padding:6px 8px;text-align:left">Reason for stopping</th>
      </tr></thead>
      <tbody>${stoppedMeds.map((m) => `
        <tr>
          <td style="padding:5px 8px;border:1px solid #ddd;font-weight:bold">${m.drug || ''}</td>
          <td style="padding:5px 8px;border:1px solid #ddd">${m.changeReason || ''}</td>
        </tr>`).join('')}
      </tbody>
    </table>`;

  const dd = dischargeDetails || {};
  const secondaryDiagRows = (dd.secondaryDiagnoses || []).filter(Boolean).map((d) => row('Secondary diagnosis', d)).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>eDischarge Summary — ${patient?.name || patient?.fullName || 'Patient'}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 13px; margin: 20px 40px; color: #222; }
    h1 { font-size: 20px; color: white; margin: 0; }
    h2 { font-size: 15px; color: #1a3a5c; border-bottom: 2px solid #1a3a5c; padding-bottom: 4px; margin-top: 20px; margin-bottom: 8px; }
    table.meta { border-collapse: collapse; width: 100%; margin-bottom: 12px; font-size: 13px; }
    .header-bar { background: #1a3a5c; padding: 12px 16px; margin-bottom: 16px; }
    .header-bar .sub { color: rgba(255,255,255,0.8); font-size: 12px; margin-top: 4px; }
    .allergy-box { background: #fff3cd; border: 1px solid #ffc107; padding: 6px 12px; border-radius: 3px; margin-bottom: 12px; font-size: 12px; }
    p { font-size: 13px; }
    @media print { @page { margin: 12mm; } }
  </style>
</head>
<body>
  <div class="header-bar">
    <h1>eDischarge Summary</h1>
    <div class="sub">${tenantDisplayName || ''} &nbsp;|&nbsp; Document created: ${today}</div>
  </div>

  <h2>Patient demographics</h2>
  <table class="meta"><tbody>
    ${row('Patient name', patient?.name || patient?.fullName)}
    ${row('Date of birth', patient?.dob)}
    ${row('Gender', patient?.gender)}
    ${row('NHS number', patient?.nhsNumber)}
    ${row('Authored by', prescriberName)}
  </tbody></table>

  <div class="allergy-box"><strong>Allergies and adverse reactions:</strong> ${allergyList}</div>

  <h2>Discharge details</h2>
  <table class="meta"><tbody>
    ${row('Discharge date', dd.dischargeDate)}
    ${row('Discharge destination', dd.destination)}
    ${row('Discharging consultant', dd.consultant)}
    ${row('Ward / specialty', dd.wardSpecialty)}
  </tbody></table>

  ${dd.primaryDiagnosis ? `
  <h2>Diagnoses</h2>
  <table class="meta"><tbody>
    ${row('Primary diagnosis', dd.primaryDiagnosis)}
    ${secondaryDiagRows}
  </tbody></table>` : ''}

  ${dd.clinicalSummary ? `
  <h2>Clinical summary</h2>
  <p>${dd.clinicalSummary.replace(/\n/g, '<br>')}</p>` : ''}

  <h2>Medications on discharge</h2>
  ${medTable}
  ${stoppedSection}

  ${dd.planForGP ? `
  <h2>Plan and requested actions</h2>
  <p>${dd.planForGP.replace(/\n/g, '<br>')}</p>` : ''}

  ${dd.communityPharmacyOdsCode ? `
  <h2>Community pharmacy referral</h2>
  <table class="meta"><tbody>
    ${row('ODS code', dd.communityPharmacyOdsCode)}
    ${row('Pharmacy name', dd.communityPharmacyName)}
  </tbody></table>` : ''}

  <h2>Person completing record</h2>
  <table class="meta"><tbody>
    ${row('Name', prescriberName)}
    ${row('Date completed', today)}
  </tbody></table>
</body>
</html>`;
}

function TtoMedicationCard({ med, onChange, onRemove }) {
  const needsReason = med.changeStatus !== 'continued';
  const needsDescription = med.changeStatus === 'dose-changed';

  const update = (field, value) => onChange({ ...med, [field]: value });

  return (
    <Card className={`mb-2 border ${med.changeStatus === 'stopped' ? 'border-danger' : 'border-secondary'}`}>
      <Card.Body className="py-2 px-3">
        <Row className="g-2 align-items-start">
          <Col xs={12} className="d-flex justify-content-between align-items-center">
            <strong className="small">{med.drug || 'Unnamed drug'}</strong>
            <Button size="sm" variant="outline-danger" className="py-0 px-1" onClick={onRemove} title="Remove from TTO">
              <i className="bi bi-x" />
            </Button>
          </Col>

          <Col xs={12} sm={4}>
            <Form.Group>
              <Form.Label className="small mb-1">Drug name</Form.Label>
              <Form.Control size="sm" value={med.drug} onChange={(e) => update('drug', e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={3}>
            <Form.Group>
              <Form.Label className="small mb-1">Dose</Form.Label>
              <Form.Control size="sm" value={med.dose} onChange={(e) => update('dose', e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={2}>
            <Form.Group>
              <Form.Label className="small mb-1">Route</Form.Label>
              <Form.Control size="sm" value={med.route} onChange={(e) => update('route', e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={3}>
            <Form.Group>
              <Form.Label className="small mb-1">Frequency</Form.Label>
              <Form.Control size="sm" value={med.frequency} onChange={(e) => update('frequency', e.target.value)} />
            </Form.Group>
          </Col>

          <Col xs={12} sm={4}>
            <Form.Group>
              <Form.Label className="small mb-1">Change status</Form.Label>
              <Form.Select size="sm" value={med.changeStatus} onChange={(e) => update('changeStatus', e.target.value)}>
                {CHANGE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>

          {needsDescription && (
            <Col xs={12} sm={8}>
              <Form.Group>
                <Form.Label className="small mb-1">Describe change <span className="text-muted">(e.g. "Dose increased from 5mg to 10mg")</span></Form.Label>
                <Form.Control size="sm" value={med.changeDescription || ''} onChange={(e) => update('changeDescription', e.target.value)} />
              </Form.Group>
            </Col>
          )}

          {needsReason && (
            <Col xs={12} sm={needsDescription ? 12 : 8}>
              <Form.Group>
                <Form.Label className="small mb-1">Reason for change <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" value={med.changeReason || ''} onChange={(e) => update('changeReason', e.target.value)} placeholder="Clinical reason" />
              </Form.Group>
            </Col>
          )}

          {med.changeStatus !== 'stopped' && (
            <>
              <Col xs={12} sm={4}>
                <Form.Group>
                  <Form.Label className="small mb-1">Supply type</Form.Label>
                  <Form.Select size="sm" value={med.supplyType || ''} onChange={(e) => update('supplyType', e.target.value)}>
                    <option value="">Select...</option>
                    {SUPPLY_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group>
                  <Form.Label className="small mb-1">Supply details</Form.Label>
                  <Form.Control size="sm" value={med.supplyDetails || ''} onChange={(e) => update('supplyDetails', e.target.value)} placeholder="e.g. 28 days, 2 blister packs" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group>
                  <Form.Label className="small mb-1">GP monitoring note</Form.Label>
                  <Form.Control size="sm" value={med.gpMonitoring || ''} onChange={(e) => update('gpMonitoring', e.target.value)} placeholder="e.g. U+E in 2 weeks" />
                </Form.Group>
              </Col>
            </>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
}

export default function DischargePrescriptionWorkspace({ show, onHide, onSave, inpatientMedications = [], patient, allergies = [], prescriberName = 'Student user', tenantDisplayName = '', dischargePrescription = null }) {
  const [ttoMedications, setTtoMedications] = useState([]);
  const [dischargeDetails, setDischargeDetails] = useState(EMPTY_DISCHARGE);
  const [showNewDrugForm, setShowNewDrugForm] = useState(false);
  const [newDrug, setNewDrug] = useState(EMPTY_NEW_DRUG);
  const [activeTab, setActiveTab] = useState('medications');

  useEffect(() => {
    if (!show) return;
    if (dischargePrescription) {
      setTtoMedications(dischargePrescription.ttoMedications || []);
      setDischargeDetails({ ...EMPTY_DISCHARGE, ...(dischargePrescription.dischargeDetails || {}) });
    } else {
      setTtoMedications([]);
      setDischargeDetails({ ...EMPTY_DISCHARGE, dischargeDate: formatIsoDateForInput(new Date()) });
    }
    setShowNewDrugForm(false);
    setNewDrug(EMPTY_NEW_DRUG);
    setActiveTab('medications');
  }, [show, dischargePrescription]);

  const ttoIds = new Set(ttoMedications.map((m) => m.sourceInpatientId).filter(Boolean));

  const chartMedications = inpatientMedications.filter((p) => p.prescriptionType !== 'fluid' && p.status !== 'stopped');

  const addToTto = (prescription) => {
    if (ttoIds.has(prescription.id)) return;
    setTtoMedications((current) => [
      ...current,
      {
        id: createTtoId(),
        sourceInpatientId: prescription.id || null,
        drug: prescription.drug || '',
        dose: prescription.dose || '',
        route: prescription.route || '',
        frequency: prescription.frequency || '',
        changeStatus: 'continued',
        changeDescription: '',
        changeReason: '',
        supplyType: '',
        supplyDetails: '',
        gpMonitoring: '',
      },
    ]);
  };

  const updateTtoMed = (id, updated) => {
    setTtoMedications((current) => current.map((m) => (m.id === id ? updated : m)));
  };

  const removeTtoMed = (id) => {
    setTtoMedications((current) => current.filter((m) => m.id !== id));
  };

  const addNewDrug = () => {
    if (!newDrug.drug.trim()) return;
    setTtoMedications((current) => [
      ...current,
      { ...newDrug, id: createTtoId(), sourceInpatientId: null },
    ]);
    setNewDrug(EMPTY_NEW_DRUG);
    setShowNewDrugForm(false);
  };

  const updateDetails = (field, value) => {
    setDischargeDetails((current) => ({ ...current, [field]: value }));
  };

  const updateSecondaryDiagnosis = (index, value) => {
    setDischargeDetails((current) => {
      const next = [...(current.secondaryDiagnoses || [''])];
      next[index] = value;
      return { ...current, secondaryDiagnoses: next };
    });
  };

  const addSecondaryDiagnosis = () => {
    setDischargeDetails((current) => ({ ...current, secondaryDiagnoses: [...(current.secondaryDiagnoses || []), ''] }));
  };

  const removeSecondaryDiagnosis = (index) => {
    setDischargeDetails((current) => ({
      ...current,
      secondaryDiagnoses: (current.secondaryDiagnoses || []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    onSave({ ttoMedications, dischargeDetails });
    onHide();
  };

  const handlePrint = () => {
    onSave({ ttoMedications, dischargeDetails });
    const html = generatePrintHTML({ ttoMedications, dischargeDetails, patient, allergies, prescriberName, tenantDisplayName });
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
  };

  const ttoCount = ttoMedications.length;
  const activeTtoCount = ttoMedications.filter((m) => m.changeStatus !== 'stopped').length;
  const stoppedTtoCount = ttoMedications.filter((m) => m.changeStatus === 'stopped').length;

  return (
    <Modal show={show} onHide={onHide} size="xl" fullscreen="lg-down" backdrop="static">
      <Modal.Header closeButton className="blue-back text-white py-2">
        <Modal.Title className="fs-6">
          Discharge prescription (TTO)
          {patient?.name || patient?.fullName ? ` — ${patient.name || patient.fullName}` : ''}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0" style={{ overflowY: 'hidden', height: 'calc(100vh - 120px)' }}>
        <Row className="g-0 h-100">
          {/* Left: inpatient chart reference */}
          <Col lg={4} className="border-end h-100 d-flex flex-column" style={{ overflowY: 'hidden' }}>
            <div className="px-3 py-2 border-bottom bg-light">
              <strong className="small">Inpatient medications</strong>
              <div className="text-muted" style={{ fontSize: '11px' }}>Click "Add to TTO" to include a drug on the discharge prescription</div>
            </div>
            <div className="px-3 py-2 flex-grow-1" style={{ overflowY: 'auto' }}>
              {chartMedications.length === 0 && (
                <p className="text-muted small mt-2">No active medications on the inpatient chart.</p>
              )}
              {chartMedications.map((med) => {
                const alreadyAdded = ttoIds.has(med.id);
                return (
                  <div key={med.id || med.drug} className="d-flex justify-content-between align-items-start gap-2 py-2 border-bottom">
                    <div className="small">
                      <div><strong>{med.drug}</strong></div>
                      <div className="text-muted">{[med.dose, med.route, med.frequency].filter(Boolean).join(' · ')}</div>
                      {med.whenRequired && <Badge bg="primary" className="me-1" style={{ fontSize: '10px' }}>PRN</Badge>}
                      {med.stat && <Badge bg="info" style={{ fontSize: '10px' }}>Stat</Badge>}
                    </div>
                    {alreadyAdded ? (
                      <Badge bg="success" className="flex-shrink-0" style={{ fontSize: '11px' }}>Added</Badge>
                    ) : (
                      <Button size="sm" variant="outline-primary" className="flex-shrink-0 py-0" style={{ fontSize: '11px' }} onClick={() => addToTto(med)}>
                        Add to TTO
                      </Button>
                    )}
                  </div>
                );
              })}

              {ttoMedications.filter((m) => !m.sourceInpatientId).length > 0 && (
                <div className="mt-3">
                  <div className="text-muted small mb-1">New drugs (not on inpatient chart):</div>
                  {ttoMedications.filter((m) => !m.sourceInpatientId).map((m) => (
                    <div key={m.id} className="small py-1 border-bottom">
                      <strong>{m.drug}</strong>
                      <div className="text-muted">{[m.dose, m.route, m.frequency].filter(Boolean).join(' · ')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>

          {/* Right: TTO builder */}
          <Col lg={8} className="h-100 d-flex flex-column" style={{ overflowY: 'hidden' }}>
            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
              <div className="border-bottom px-3 pt-2">
                <Nav variant="tabs" className="border-0">
                  <Nav.Item>
                    <Nav.Link eventKey="medications" className="small py-1 px-3">
                      TTO medications
                      {ttoCount > 0 && <Badge bg="secondary" className="ms-1" style={{ fontSize: '10px' }}>{ttoCount}</Badge>}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="details" className="small py-1 px-3">
                      Discharge details
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>

              <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
                <Tab.Content className="h-100">
                  {/* TTO Medications tab */}
                  <Tab.Pane eventKey="medications" className="p-3">
                    {ttoCount === 0 && (
                      <Alert variant="info" className="small py-2">
                        No drugs added yet. Use "Add to TTO" on medications in the inpatient chart, or add a new drug below.
                      </Alert>
                    )}

                    {ttoCount > 0 && (
                      <div className="small text-muted mb-2">
                        {activeTtoCount} medication{activeTtoCount !== 1 ? 's' : ''} on discharge
                        {stoppedTtoCount > 0 && ` · ${stoppedTtoCount} stopped`}
                      </div>
                    )}

                    {ttoMedications.map((med) => (
                      <TtoMedicationCard
                        key={med.id}
                        med={med}
                        onChange={(updated) => updateTtoMed(med.id, updated)}
                        onRemove={() => removeTtoMed(med.id)}
                      />
                    ))}

                    {!showNewDrugForm && (
                      <Button size="sm" variant="outline-secondary" className="mt-1" onClick={() => setShowNewDrugForm(true)}>
                        + Add drug not on chart
                      </Button>
                    )}

                    {showNewDrugForm && (
                      <Card className="mt-2 border-secondary">
                        <Card.Body className="py-2 px-3">
                          <div className="small fw-bold mb-2">New drug (not on inpatient chart)</div>
                          <Row className="g-2">
                            <Col xs={12} sm={4}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Drug name <span className="text-danger">*</span></Form.Label>
                                <Form.Control size="sm" value={newDrug.drug} onChange={(e) => setNewDrug((c) => ({ ...c, drug: e.target.value }))} placeholder="e.g. Paracetamol" />
                              </Form.Group>
                            </Col>
                            <Col xs={6} sm={3}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Dose</Form.Label>
                                <Form.Control size="sm" value={newDrug.dose} onChange={(e) => setNewDrug((c) => ({ ...c, dose: e.target.value }))} placeholder="e.g. 500mg" />
                              </Form.Group>
                            </Col>
                            <Col xs={6} sm={2}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Route</Form.Label>
                                <Form.Control size="sm" value={newDrug.route} onChange={(e) => setNewDrug((c) => ({ ...c, route: e.target.value }))} placeholder="Oral" />
                              </Form.Group>
                            </Col>
                            <Col xs={12} sm={3}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Frequency</Form.Label>
                                <Form.Control size="sm" value={newDrug.frequency} onChange={(e) => setNewDrug((c) => ({ ...c, frequency: e.target.value }))} placeholder="QDS" />
                              </Form.Group>
                            </Col>
                            <Col xs={12} sm={4}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Supply type</Form.Label>
                                <Form.Select size="sm" value={newDrug.supplyType} onChange={(e) => setNewDrug((c) => ({ ...c, supplyType: e.target.value }))}>
                                  <option value="">Select...</option>
                                  {SUPPLY_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col xs={12} sm={8}>
                              <Form.Group>
                                <Form.Label className="small mb-1">Reason / indication</Form.Label>
                                <Form.Control size="sm" value={newDrug.changeReason} onChange={(e) => setNewDrug((c) => ({ ...c, changeReason: e.target.value }))} placeholder="Why is this being started?" />
                              </Form.Group>
                            </Col>
                          </Row>
                          <div className="d-flex gap-2 mt-2">
                            <Button size="sm" variant="primary" onClick={addNewDrug} disabled={!newDrug.drug.trim()}>Add to TTO</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => { setShowNewDrugForm(false); setNewDrug(EMPTY_NEW_DRUG); }}>Cancel</Button>
                          </div>
                        </Card.Body>
                      </Card>
                    )}
                  </Tab.Pane>

                  {/* Discharge details tab */}
                  <Tab.Pane eventKey="details" className="p-3">
                    <Row className="g-3">
                      <Col xs={6} md={3}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Discharge date</Form.Label>
                          <Form.Control type="date" size="sm" value={dischargeDetails.dischargeDate} onChange={(e) => updateDetails('dischargeDate', e.target.value)} />
                        </Form.Group>
                      </Col>
                      <Col xs={6} md={4}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Discharge destination</Form.Label>
                          <Form.Select size="sm" value={dischargeDetails.destination} onChange={(e) => updateDetails('destination', e.target.value)}>
                            <option value="">Select...</option>
                            {DISCHARGE_DESTINATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={5}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Discharging consultant</Form.Label>
                          <Form.Control size="sm" value={dischargeDetails.consultant} onChange={(e) => updateDetails('consultant', e.target.value)} placeholder="e.g. Dr Smith" />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Ward / specialty</Form.Label>
                          <Form.Control size="sm" value={dischargeDetails.wardSpecialty} onChange={(e) => updateDetails('wardSpecialty', e.target.value)} placeholder="e.g. Ward 12, Acute Medicine" />
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <hr className="my-1" />
                        <div className="small fw-bold mb-2">Diagnoses</div>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Primary diagnosis</Form.Label>
                          <Form.Control size="sm" value={dischargeDetails.primaryDiagnosis} onChange={(e) => updateDetails('primaryDiagnosis', e.target.value)} placeholder="e.g. Infective exacerbation of COPD" />
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Label className="small mb-1">Secondary diagnoses</Form.Label>
                        {(dischargeDetails.secondaryDiagnoses || ['']).map((diag, index) => (
                          <div key={index} className="d-flex gap-2 mb-1">
                            <Form.Control size="sm" value={diag} onChange={(e) => updateSecondaryDiagnosis(index, e.target.value)} placeholder={`Secondary diagnosis ${index + 1}`} />
                            {(dischargeDetails.secondaryDiagnoses || []).length > 1 && (
                              <Button size="sm" variant="outline-danger" className="py-0 px-1" onClick={() => removeSecondaryDiagnosis(index)}>
                                <i className="bi bi-x" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button size="sm" variant="outline-secondary" className="mt-1" onClick={addSecondaryDiagnosis}>
                          + Add secondary diagnosis
                        </Button>
                      </Col>

                      <Col xs={12}>
                        <hr className="my-1" />
                        <Form.Group>
                          <Form.Label className="small mb-1">Clinical summary</Form.Label>
                          <Form.Control as="textarea" rows={3} size="sm" value={dischargeDetails.clinicalSummary} onChange={(e) => updateDetails('clinicalSummary', e.target.value)} placeholder="Brief summary of the admission, treatment, and response..." />
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Plan and actions for GP</Form.Label>
                          <Form.Control as="textarea" rows={3} size="sm" value={dischargeDetails.planForGP} onChange={(e) => updateDetails('planForGP', e.target.value)} placeholder="e.g. Please check U+E in 2 weeks. Referral made to community COPD nurse..." />
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <hr className="my-1" />
                        <div className="small fw-bold mb-2">Community pharmacy referral (Discharge Medicines Service)</div>
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Group>
                          <Form.Label className="small mb-1">ODS code</Form.Label>
                          <Form.Control size="sm" value={dischargeDetails.communityPharmacyOdsCode} onChange={(e) => updateDetails('communityPharmacyOdsCode', e.target.value)} placeholder="e.g. FQ123" />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={8}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Pharmacy name</Form.Label>
                          <Form.Control size="sm" value={dischargeDetails.communityPharmacyName} onChange={(e) => updateDetails('communityPharmacyName', e.target.value)} placeholder="e.g. Boots Pharmacy, High Street" />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab.Pane>
                </Tab.Content>
              </div>
            </Tab.Container>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer className="py-2">
        <Button variant="outline-secondary" size="sm" onClick={onHide}>Close</Button>
        <Button variant="outline-primary" size="sm" onClick={handleSave}>Save draft</Button>
        <Button variant="primary" size="sm" onClick={handlePrint}>
          <i className="bi bi-printer me-1" />
          Save &amp; print
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
